/**
 * Component: Compatible Products Form V4 (Compact)
 * Focused on information density and minimal UI.
 */

if (!customElements.get('compatible-products-form')) {
    customElements.define('compatible-products-form', class CompatibleProductsForm extends HTMLElement {
        constructor() {
            super();
            this._boundUpdateTotal = this.updateTotal.bind(this);
        }

        connectedCallback() {
            this.initElements();
            this.initEvents();
            // restoreFromStorage removed as per user request to have none selected by default.
            setTimeout(() => this.updateTotal(), 50);
        }

        initElements() {
            this.cards = this.querySelectorAll('.bundle-card');
            this.checkboxes = this.querySelectorAll('.bundle-card__checkbox');
            this.qtyInputs = this.querySelectorAll('[data-qty-input]');
            this.plusBtns = this.querySelectorAll('[data-qty-plus]');
            this.minusBtns = this.querySelectorAll('[data-qty-minus]');
            this.submitButton = this.querySelector('[data-bundle-submit]');
            this.loadMoreButton = this.querySelector('[data-action="load-more"]');
            this.thumbsContainer = this.querySelector('[data-selected-thumbs]');
            
            this.totalPriceLabel = this.querySelector('[data-total-price]');
            this.totalCompareLabel = this.querySelector('[data-total-compare]');
            this.totalSavingsLabel = this.querySelector('[data-total-savings]');
            this.selectedCountLabels = this.querySelectorAll('[data-selected-count]');
            
            this.progressLine = this.querySelector('[data-bundle-progress]');
            this.savingsPercentLabel = this.querySelector('[data-bundle-savings-percent]');
            this.savingsTip = this.querySelector('[data-bundle-savings-tip]');
            this.totalFinalLabel = this.querySelector('[data-total-final]');
            this.pillBadge = this.querySelector('.bundle-module__pill-badge');
            this.subheading = this.querySelector('.bundle-module__subheading');
            this.footerPriceSection = this.querySelector('.bundle-footer__price-info');
        }

        initEvents() {
            this.checkboxes.forEach(cb => cb.addEventListener('change', () => this.updateTotal()));

            // Removed config drawer listeners since we now use external Quick View.

            this.plusBtns.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const input = btn.parentNode.querySelector('[data-qty-input]');
                    input.value = parseInt(input.value) + 1;
                    this.updateTotal();
                });
            });

            this.minusBtns.forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const input = btn.parentNode.querySelector('[data-qty-input]');
                    if (parseInt(input.value) > 1) {
                        input.value = parseInt(input.value) - 1;
                        this.updateTotal();
                    }
                });
            });

            this.qtyInputs.forEach(input => {
                input.addEventListener('change', (e) => {
                    if (parseInt(input.value) < 1) input.value = 1;
                    this.updateTotal();
                });
                input.addEventListener('click', (e) => e.stopPropagation());
            });

            document.addEventListener('bundle:variantSelected', (e) => {
                if (e.detail && e.detail.bundleProductId) {
                    this.handleQuickViewSelection(e.detail);
                }
            });

            this.cards.forEach(card => {
                card.addEventListener('click', (e) => {
                    if (e.target.closest('.bundle-card__config-wrapper') || e.target.closest('.bundle-card__qty-selector')) return;
                    
                    const checkbox = card.querySelector('.bundle-card__checkbox');
                    if (checkbox) {
                        checkbox.checked = !checkbox.checked;
                        checkbox.dispatchEvent(new Event('change'));
                        this.saveToStorage();
                    }
                });
            });

            if (this.submitButton) {
                this.submitButton.addEventListener('click', (e) => this.handleAddToCart(e));
            }
            
            if (this.loadMoreButton) {
                this.loadMoreButton.addEventListener('click', () => {
                    this.cards.forEach(card => {
                        card.style.display = 'flex';
                        card.removeAttribute('data-hidden-card');
                    });
                    this.loadMoreButton.style.display = 'none';
                });
            }
        }

        handleQuickViewSelection(detail) {
            const card = Array.from(this.cards).find(c => c.dataset.productId == detail.bundleProductId);
            if (!card) return;

            // Update data attributes
            card.dataset.variantId = detail.variantId;
            card.dataset.price = detail.price;
            card.dataset.comparePrice = detail.compareAtPrice || detail.price;

            // Update visual price
            const priceEl = card.querySelector('[data-item-price]');
            if (priceEl) priceEl.textContent = this.formatMoney(detail.price);

            const compareEl = card.querySelector('[data-item-compare-price]');
            if (compareEl) {
                if (detail.compareAtPrice && detail.compareAtPrice > detail.price) {
                    compareEl.textContent = this.formatMoney(detail.compareAtPrice);
                    compareEl.style.display = 'inline-block';
                } else {
                    compareEl.style.display = 'none';
                }
            }

            // Update image
            if (detail.imageUrl) {
                const img = card.querySelector('.bundle-card__img');
                if (img) img.src = detail.imageUrl;
            }

            // Update variant label
            const variantEl = card.querySelector('[data-variant-display]');
            if (variantEl) variantEl.textContent = detail.title;

            const skuEl = card.querySelector('[data-sku-display]');
            if (skuEl && detail.sku) skuEl.textContent = detail.sku;

            // Update checkbox value
            const checkbox = card.querySelector('.bundle-card__checkbox');
            if (checkbox) checkbox.value = detail.variantId;

            // Update trigger attributes so it opens with the new selection next time
            const trigger = card.querySelector('[data-bundle-quick-view-trigger]');
            if (trigger) {
                trigger.setAttribute('data-bundle-variant-id', detail.variantId);
                const btnSpan = trigger.querySelector('span');
                if (btnSpan) btnSpan.textContent = 'Edit Options';
            }

            // Update quantity if passed
            if (detail.qty) {
                card.setAttribute('data-qty', detail.qty);
                const trigger = card.querySelector('[data-bundle-quick-view-trigger]');
                if (trigger) trigger.setAttribute('data-bundle-qty', detail.qty);
            }

            // Always select the card if a variant was chosen
            if (checkbox && !checkbox.checked) {
                checkbox.checked = true;
                checkbox.dispatchEvent(new Event('change'));
            }

            this.updateTotal();
            this.saveToStorage();
        }

        updateTotal() {
            let subTotal = 0;
            let compareTotal = 0;
            let count = 0;
            const selectedImages = [];

            this.cards.forEach(card => {
                const checkbox = card.querySelector('.bundle-card__checkbox');
                const price = Number(card.getAttribute('data-price')) || 0;
                let cPrice = Number(card.getAttribute('data-compare-price')) || price;
                const qty = Number(card.getAttribute('data-qty')) || 1;
                
                if (checkbox && checkbox.checked) {
                    if (cPrice < price) cPrice = price;
                    subTotal += (price * qty);
                    compareTotal += (cPrice * qty);
                    count += qty;
                    card.classList.add('is-selected');

                    const trigger = card.querySelector('[data-bundle-quick-view-trigger]');
                    if (trigger) {
                         const triggerSpan = trigger.querySelector('span');
                         if (triggerSpan) triggerSpan.textContent = 'Edit Options';
                         trigger.setAttribute('data-bundle-qty', qty);
                    }

                    const img = card.querySelector('.bundle-card__img')?.src;
                    if (img) selectedImages.push({ src: img, qty: qty, idx: Array.from(this.cards).indexOf(card) });
                } else {
                    card.classList.remove('is-selected');
                    const trigger = card.querySelector('[data-bundle-quick-view-trigger]');
                    if (trigger) {
                        const triggerSpan = trigger.querySelector('span');
                        if (triggerSpan) triggerSpan.textContent = 'Choose Options';
                        trigger.setAttribute('data-bundle-qty', 1);
                    }
                }
            });

            this.renderThumbnails(selectedImages);

            let discountPercent = 0;
            const subTotalFormatted = subTotal / 100;

            if (subTotalFormatted >= 101) discountPercent = 14;
            else if (subTotalFormatted >= 51) discountPercent = 10;
            else if (subTotalFormatted >= 1) discountPercent = 5;

            this.currentDiscountPercent = discountPercent;

            const savings = Math.floor(subTotal * (discountPercent/100));
            const final = subTotal - savings;
            const totalActualSavings = compareTotal - final;

            // Update UI based on selection state
            if (this.pillBadge) {
                if (count === 0) {
                    this.pillBadge.classList.add('is-empty');
                    this.classList.add('is-empty-selection');
                } else {
                    this.pillBadge.classList.remove('is-empty');
                    this.classList.remove('is-empty-selection');
                }
            }
            this.selectedCountLabels.forEach(el => el.textContent = count);

            if (this.subheading) {
                this.subheading.textContent = 'Build your bundle and save up to 14%';
            }

            const percentWrap = this.querySelector('[data-bundle-savings-percent-wrap]');
            let tip = "Add items to unlock your first discount";
            let progress = 0;

            if (count === 0) {
                if (percentWrap) percentWrap.innerHTML = `Bundle savings available`;
                tip = "Add items to unlock your first discount";
                progress = 0;
            } else {
                if (percentWrap) percentWrap.innerHTML = `Discount: <strong>${discountPercent}%</strong>`;
                
                if (subTotalFormatted < 51) {
                    progress = (subTotalFormatted / 101) * 100;
                    tip = `You're $${(51 - subTotalFormatted).toFixed(2)} away from 10% off`;
                } else if (subTotalFormatted < 101) {
                    progress = (subTotalFormatted / 101) * 100;
                    tip = `Add $${(101 - subTotalFormatted).toFixed(2)} more to unlock the full 14% savings`;
                } else {
                    progress = 100;
                    tip = "Maximum bundle savings unlocked";
                }
            }

            // Update summary
            if (this.totalCompareLabel) this.totalCompareLabel.textContent = this.formatMoney(subTotal);
            if (this.totalSavingsLabel) this.totalSavingsLabel.textContent = this.formatMoney(totalActualSavings);
            if (this.totalFinalLabel) this.totalFinalLabel.textContent = this.formatMoney(final);

            if (this.footerPriceSection) {
                this.footerPriceSection.style.display = count === 0 ? 'none' : 'flex';
            }

            this.renderProgressBar(progress, tip);

            if (this.submitButton) {
                this.submitButton.disabled = count === 0;
                const ctaSpan = this.submitButton.querySelector('span');
                if (ctaSpan) {
                    if (count === 0) {
                        ctaSpan.textContent = 'Choose items to start saving';
                        this.submitButton.classList.remove('is-populated');
                    } else {
                        ctaSpan.textContent = count === 1 ? 'Add item to cart' : 'Add Bundle to cart';
                        this.submitButton.classList.add('is-populated');
                    }
                }
            }
            this.saveToStorage();
        }

        renderProgressBar(progress, tip) {
            const finalProgress = isNaN(progress) ? 0 : Math.min(100, Math.max(0, progress));
            
            const progressElements = document.querySelectorAll('[data-bundle-progress]');
            progressElements.forEach(el => {
                const isFull = finalProgress >= 100;
                const gradient = isFull 
                    ? 'linear-gradient(90deg, #10b981, #34d399)' 
                    : 'linear-gradient(90deg, #1d4ed8 0%, #2563eb 55%, #3b82f6 100%)';
                
                el.style.width = finalProgress + '%';
                el.setAttribute('style', `width: ${finalProgress}%; background: ${gradient} !important; border-radius: 999px; height: 100% !important; display: block; position: absolute; top: 0; left: 0; padding: 0 !important; margin: 0 !important;`);
            });
            
            if (this.savingsTip) {
                this.savingsTip.textContent = tip;
                if (progress >= 100) {
                    this.savingsTip.style.color = '#16a34a'; // Green color for success
                    this.savingsTip.style.fontWeight = '700';
                } else {
                    this.savingsTip.style.color = ''; // Reset
                    this.savingsTip.style.fontWeight = '';
                }
            }
        }

        renderThumbnails(selectedItems) {
            if (!this.thumbsContainer) return;
            this.thumbsContainer.innerHTML = '';
            
            if (selectedItems.length === 0) {
                this.thumbsContainer.style.display = 'none';
                return;
            }

            this.thumbsContainer.style.display = 'grid';
            
            selectedItems.forEach(item => {
                const thumb = document.createElement('div');
                thumb.className = 'bundle-footer__thumb';
                thumb.setAttribute('data-target-card', item.idx);
                thumb.innerHTML = `
                    <div class="bundle-footer__thumb-inner">
                        <img src="${item.src}" alt="Selected item">
                        ${item.qty > 1 ? `<span class="bundle-footer__thumb-badge">${item.qty}</span>` : ''}
                        <div class="bundle-footer__thumb-remove">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                        </div>
                    </div>
                `;
                this.thumbsContainer.appendChild(thumb);
            });

            // Delegate remove
            this.thumbsContainer.querySelectorAll('.bundle-footer__thumb-remove').forEach(btn => {
                btn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    const idx = btn.closest('.bundle-footer__thumb').dataset.targetCard;
                    const card = this.cards[idx];
                    const cb = card?.querySelector('.bundle-card__checkbox');
                    if (cb) {
                        cb.checked = false;
                        cb.dispatchEvent(new Event('change'));
                        this.saveToStorage();
                    }
                });
            });
        }

        async handleAddToCart(e) {
            e.preventDefault();
            const ctaSpan = this.submitButton.querySelector('span');
            const originalText = ctaSpan ? ctaSpan.textContent : 'Add Bundle to cart';
            
            if (ctaSpan) ctaSpan.textContent = 'Adding...';
            this.submitButton.disabled = true;

            const items = [];
            const bundle_id = `bundle_${Date.now()}`;
            const discountPercent = this.currentDiscountPercent || 0;
            
            this.cards.forEach(card => {
                if (card.classList.contains('is-selected')) {
                    const quantity = parseInt(card.getAttribute('data-qty') || 1);
                    items.push({
                        id: parseInt(card.dataset.variantId),
                        quantity: quantity,
                        properties: { 
                            '_bundle_id': bundle_id,
                            '_incentivized': 'true',
                            '_discount_percent': discountPercent.toString()
                        }
                    });
                }
            });

            const cartDrawer = document.querySelector('cart-drawer');
            const sections = cartDrawer ? cartDrawer.getSectionsToRender().map((section) => section.id) : [];

            try {
                const response = await fetch('/cart/add.js', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ 
                        items,
                        sections: sections,
                        sections_url: window.location.pathname
                    })
                });
                
                const parsedResponse = await response.json();

                if (parsedResponse.status && parsedResponse.status !== 200) {
                    throw new Error(parsedResponse.description || parsedResponse.message);
                }

                if (cartDrawer) {
                    cartDrawer.renderContents(parsedResponse);
                    document.dispatchEvent(new CustomEvent('cart:refresh'));
                } else {
                    window.location.href = '/cart';
                }
            } catch (err) {
                console.error(err);
                this.submitButton.disabled = false;
                this.submitButton.innerText = 'Add Items';
                alert(err.message || 'Error adding to cart. Please try again.');
            } finally {
                this.updateTotal();
            }
        }

        saveToStorage() {
            const state = {};
            this.cards.forEach((card, idx) => {
                state[idx] = {
                    selected: card.classList.contains('is-selected'),
                    variantId: card.dataset.variantId,
                    qty: card.getAttribute('data-qty') || 1,
                    price: card.dataset.price,
                    compareAtPrice: card.dataset.comparePrice,
                    title: card.querySelector('[data-variant-display]')?.textContent,
                    sku: card.querySelector('[data-sku-display]')?.textContent,
                    imageUrl: card.querySelector('.bundle-card__img')?.src
                };
            });
            localStorage.setItem('caco_bundle_v4', JSON.stringify(state));
        }

        restoreFromStorage() {
            try {
                const raw = localStorage.getItem('caco_bundle_v4');
                if (!raw) return;
                const state = JSON.parse(raw);
                this.cards.forEach((card, idx) => {
                    const data = state[idx];
                    if (!data) return;

                    // Restore visual config if it was a custom variant selection
                    if (data.variantId && data.variantId !== card.dataset.baseVariantId) {
                        this.handleQuickViewSelection({
                            bundleProductId: card.dataset.productId,
                            variantId: data.variantId,
                            price: data.price,
                            compareAtPrice: data.compareAtPrice,
                            title: data.title,
                            sku: data.sku,
                            qty: data.qty,
                            imageUrl: data.imageUrl
                        });
                    } else if (data.qty) {
                        // Just qty update if no custom variant
                        card.setAttribute('data-qty', data.qty);
                    }

                    const cb = card.querySelector('.bundle-card__checkbox');
                    if (cb) cb.checked = data.selected;
                });
            } catch (e) { console.error('Restore Error:', e); }
        }

        formatMoney(cents) {
            const format = window.theme?.moneyFormat || "${{amount}}";
            let amount = (cents / 100).toFixed(2);
            return format.replace('{{amount}}', amount).replace('${{amount}}', '$' + amount);
        }
    });
}
